import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { apiFetch } from '../../utils/api'
import styles from './SickLeavePage.module.css'

interface Absence { id: string; employee_name: string; type: string; start_date: string; end_date: string; days_count: number; status: string }
interface Employee { id: string; firstName: string; lastName: string }

export function SickLeavePage() {
  const { t } = useTranslation()
  const [activeView, setActiveView] = useState<'sick' | 'leave'>('sick')
  const [absences, setAbsences] = useState<Absence[]>([])
  const [employees, setEmployees] = useState<Employee[]>([])
  const [showForm, setShowForm] = useState(false)
  const [formEmpId, setFormEmpId] = useState('')
  const [formStart, setFormStart] = useState('')
  const [formEnd, setFormEnd] = useState('')
  const [formNotes, setFormNotes] = useState('')
  const [formType, setFormType] = useState('conge_annuel')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    apiFetch<{ employees: Employee[] }>('/employees?active=true').then(r => { if (r.data) setEmployees(r.data.employees) })
    loadData()
  }, [])

  const loadData = async () => {
    if (activeView === 'sick') {
      const r = await apiFetch<{ sickLeaves: Absence[] }>('/absences/sick-leaves')
      if (r.data) setAbsences(r.data.sickLeaves)
    } else {
      const r = await apiFetch<{ leaveRequests: Absence[] }>('/absences/leave-requests')
      if (r.data) setAbsences(r.data.leaveRequests)
    }
  }

  useEffect(() => { loadData() }, [activeView])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    const endpoint = activeView === 'sick' ? '/absences/sick-leave' : '/absences/leave-request'
    await apiFetch(endpoint, {
      method: 'POST',
      body: JSON.stringify({
        employeeId: formEmpId,
        startDate: formStart,
        endDate: formEnd,
        type: activeView === 'leave' ? formType : undefined,
        notes: formNotes || undefined,
        reason: formNotes || undefined,
      }),
    })
    setShowForm(false)
    setFormEmpId(''); setFormStart(''); setFormEnd(''); setFormNotes('')
    setSubmitting(false)
    loadData()
  }

  const handleApprove = async (id: string) => { await apiFetch(`/absences/${id}/approve`, { method: 'PUT' }); loadData() }
  const handleReject = async (id: string) => { await apiFetch(`/absences/${id}/reject`, { method: 'PUT' }); loadData() }

  const fmtDate = (d: string) => new Date(d).toLocaleDateString()

  return (
    <div className={styles.page}>
      <div className={styles.header}><h1>{t('absences.title')}</h1></div>

      <div className={styles.tabs}>
        <button className={`${styles.tab} ${activeView === 'sick' ? styles.tabActive : ''}`} onClick={() => { setActiveView('sick'); setShowForm(false) }}>
          {t('absences.tab_sick')}
        </button>
        <button className={`${styles.tab} ${activeView === 'leave' ? styles.tabActive : ''}`} onClick={() => { setActiveView('leave'); setShowForm(false) }}>
          {t('absences.tab_leave')}
        </button>
      </div>

      <div className={styles.actions}>
        <button className={styles.actionBtn} onClick={() => setShowForm(!showForm)}>
          {activeView === 'sick' ? t('absences.declare_sick') : t('absences.request_leave')}
        </button>
      </div>

      {showForm && (
        <form className={styles.counterCard} onSubmit={handleSubmit}>
          <select className={styles.formInput} value={formEmpId} onChange={e => setFormEmpId(e.target.value)} required>
            <option value="">-- {t('employees.title')} --</option>
            {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.lastName} {emp.firstName}</option>)}
          </select>
          {activeView === 'leave' && (
            <select className={styles.formInput} value={formType} onChange={e => setFormType(e.target.value)}>
              <option value="conge_annuel">{t('absences.entitlement')}</option>
              <option value="conge_special">{t('absences.tab_leave')}</option>
              <option value="sans_solde">{t('absences.tab_leave')}</option>
            </select>
          )}
          <div className={styles.counterValues}>
            <input type="date" className={styles.formInput} value={formStart} onChange={e => setFormStart(e.target.value)} required />
            <input type="date" className={styles.formInput} value={formEnd} onChange={e => setFormEnd(e.target.value)} required />
          </div>
          <input type="text" className={styles.formInput} value={formNotes} onChange={e => setFormNotes(e.target.value)} placeholder="Notes..." />
          <button type="submit" className={styles.actionBtn} disabled={submitting}>
            {submitting ? t('common.loading') : t('common.confirm')}
          </button>
        </form>
      )}

      {absences.length === 0 ? (
        <div className={styles.empty}><p>{activeView === 'sick' ? t('absences.no_sick_leaves') : t('absences.no_leave_requests')}</p></div>
      ) : (
        <div className={styles.list}>
          {absences.map(a => (
            <div key={a.id} className={styles.listItem}>
              <div className={styles.listItemMain}>
                <span className={styles.listItemName}>{a.employee_name}</span>
                <span className={styles.listItemDates}>{fmtDate(a.start_date)} — {fmtDate(a.end_date)} ({a.days_count}j)</span>
              </div>
              <div className={styles.listItemActions}>
                <span className={styles.badge}>{a.status}</span>
                {a.status === 'pending' && (
                  <>
                    <button className={styles.approveBtn} onClick={() => handleApprove(a.id)}>OK</button>
                    <button className={styles.rejectBtn} onClick={() => handleReject(a.id)}>X</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
